/**
 * Employee profile photo selector.
 * The parent saves the selected file through the employee API.
 */

import React, { useEffect, useState } from 'react';
import {
  Box,
  Card,
  CardContent,
  Avatar,
  Button,
  CircularProgress,
  Alert,
  Typography,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
} from '@mui/material';
import PhotoCameraIcon from '@mui/icons-material/PhotoCamera';
import DeleteIcon from '@mui/icons-material/Delete';
import {
  FILE_SIZE_LIMITS,
  ALLOWED_MIME_TYPES,
} from '../api/azureBlobStorageApi';

interface EmployeeProfilePhotoUploadProps {
  employeeId: string;
  currentPhotoUrl?: string;
  onPhotoUpload?: (fileInfo: { url: string; blobName: string; file?: File }) => void | string | Promise<void | string>;
  /** Removes the stored photo. When omitted the Delete button is not rendered at all, so the
   *  control is never shown for a screen that cannot actually perform the deletion. */
  onPhotoDelete?: () => void | Promise<void>;
}

const EmployeeProfilePhotoUpload: React.FC<
  EmployeeProfilePhotoUploadProps
> = ({ employeeId, currentPhotoUrl, onPhotoUpload, onPhotoDelete }) => {
  const [previewUrl, setPreviewUrl] = useState<string | undefined>(
    currentPhotoUrl
  );
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [uploading, setUploading] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!selectedFile) {
      setPreviewUrl(currentPhotoUrl);
    }
  }, [currentPhotoUrl, selectedFile]);

  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    // Validate file type
    if (!ALLOWED_MIME_TYPES.IMAGES.includes(file.type)) {
      setError('Only image files (JPEG, PNG, GIF, WEBP) are allowed');
      return;
    }

    // Validate file size
    if (file.size > FILE_SIZE_LIMITS.IMAGES) {
      setError(`File size exceeds ${(FILE_SIZE_LIMITS.IMAGES / 1024 / 1024).toFixed(2)}MB limit`);
      return;
    }

    setError(null);

    // Create preview
    const reader = new FileReader();
    reader.onload = (e) => {
      setPreviewUrl(e.target?.result as string);
      setSelectedFile(file);
    };
    reader.readAsDataURL(file);
  };

  const handleUpload = async () => {
    if (!selectedFile) return;

    setUploading(true);
    setError(null);

    try {
      const uploadedUrl = await onPhotoUpload?.({
        url: previewUrl || '',
        blobName: '',
        file: selectedFile,
      });
      if (uploadedUrl) {
        setPreviewUrl(uploadedUrl);
      }
      setSelectedFile(null);
    } catch (err) {
      console.error('Upload error:', err);
      setError('An error occurred during upload');
    } finally {
      setUploading(false);
    }
  };

  const handleDeleteConfirm = async () => {
    if (!onPhotoDelete) return;

    setDeleteDialogOpen(false);
    setDeleting(true);
    setError(null);

    try {
      await onPhotoDelete();
      setSelectedFile(null);
      setPreviewUrl(undefined);
    } catch (err) {
      console.error('Delete error:', err);
      setError('Could not delete the photo. Please try again.');
    } finally {
      setDeleting(false);
    }
  };

  const busy = uploading || deleting;

  return (
    <Card sx={{ boxShadow: 2, borderRadius: 3, p: 1.5, maxWidth: 260, mx: 'auto' }}>
      <CardContent sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', p: 1.5, gap: 1 }}>
        {/* Photo Preview */}

        <Box sx={{ position: 'relative', display: 'inline-block', mb: 1 }}>
          <Avatar
            src={previewUrl}
            sx={{
              width: 110,
              height: 110,
              opacity: busy ? 0.5 : 1,
              border: '2px solid #e0e0e0',
              boxShadow: 1,
              transition: 'opacity 0.2s',
            }}
          />
          {busy && (
            <CircularProgress size={40} sx={{
              position: 'absolute',
              top: '50%',
              left: '50%',
              transform: 'translate(-50%, -50%)',
              zIndex: 2
            }} />
          )}
        </Box>

        {/* File Input & Buttons */}
        <Box sx={{ display: 'flex', gap: 1, mt: 1, flexWrap: 'wrap', justifyContent: 'center' }}>
          <input
            type="file"
            accept="image/*"
            onChange={handleFileSelect}
            id="photo-input"
            style={{ display: 'none' }}
          />
          <label htmlFor="photo-input">
            <Button
              variant="outlined"
              component="span"
              startIcon={<PhotoCameraIcon />}
              disabled={busy}
              size="small"
              sx={{ minWidth: 80, px: 1.5, mb: { xs: 1, sm: 0 } }}
              tabIndex={busy ? -1 : 0}
            >
              Select
            </Button>
          </label>
          {selectedFile && (
            <Button
              variant="contained"
              color="primary"
              onClick={handleUpload}
              disabled={busy}
              size="small"
              sx={{ minWidth: 80, px: 1.5, mb: { xs: 1, sm: 0 } }}
              startIcon={uploading ? <CircularProgress size={16} /> : undefined}
              tabIndex={busy ? -1 : 0}
            >
              {uploading ? 'Uploading' : 'Upload'}
            </Button>
          )}
          {currentPhotoUrl && onPhotoDelete && (
            <Button
              variant="outlined"
              color="error"
              onClick={() => setDeleteDialogOpen(true)}
              disabled={busy}
              size="small"
              sx={{ minWidth: 80, px: 1.5, mb: { xs: 1, sm: 0 } }}
              startIcon={deleting ? <CircularProgress size={16} /> : <DeleteIcon />}
              tabIndex={busy ? -1 : 0}
            >
              {deleting ? 'Deleting' : 'Delete'}
            </Button>
          )}
        </Box>

        {/* Error Message */}
        {error && (
          <Alert severity="error" onClose={() => setError(null)} sx={{ mt: 1, width: '100%' }}>
            {error}
          </Alert>
        )}

        {/* File Info */}
        {selectedFile && (
          <Typography variant="caption" color="textSecondary" sx={{ mt: 0.5 }}>
            {selectedFile.name} ({(selectedFile.size / 1024).toFixed(2)}KB)
          </Typography>
        )}
      </CardContent>

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
      >
        <DialogTitle>Delete Photo</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to delete this photo?
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)} size="small">Cancel</Button>
          <Button onClick={handleDeleteConfirm} color="error" autoFocus size="small">
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </Card>
  );
};

export default EmployeeProfilePhotoUpload;
